<!--
  TeamAttestationList.vue -- a team member's "already approved" statement on
  the Approval card, and retracting it.

  Bug Intake #578 follow-up (Jake, 2026-10-29). At upload a member of the team may
  state that the stitch file was already approved before it came to
  ThreadFlow; that statement counts as approved in every store that uses the
  file, at its own size. This lists each such statement (who vouched, who
  recorded it) and offers Retract to those who may use it: the member who
  vouched, an Owner, or a Super Admin. The API decides that per statement
  (`can_retract`, the same rule the retract endpoint enforces), so the page
  never re-derives it.

  What retracting does (the confirm step says so before anything is sent):
  the statement stops approving NEW placements, which then need the client's
  approval. Sizes it already approved stay approved; when stores rely on
  them, the result names those stores (live ones flagged) so the
  distributor can follow up. After a retract the list is re-read from the
  server and the parent is told (`retracted`), so it can re-read its cards.

  Reads GET  /api/artworks/<id>/prior-approval/team-attestations
  Writes POST /api/artworks/<id>/prior-approval/team-attestations/<att>/retract
-->
<template>
    <div
        v-if="rows.length || kept"
        class="tal"
        data-testid="dla-team-attestations"
    >
        <div
            v-for="row in rows"
            :key="row.id"
            class="tal__row"
            :data-testid="`dla-team-attestation-${row.id}`"
        >
            <div class="tal__line">
                <span class="tal__chip">{{ statementLabel(row) }}</span>
                <span class="tal__who">recorded by {{ row.recorded_by_name }}</span>
                <button
                    v-if="row.can_retract && openId !== row.id"
                    type="button"
                    class="tal__btn tal__btn--link"
                    :data-testid="`dla-team-attestation-retract-open-${row.id}`"
                    :disabled="submitting"
                    @click="open(row)"
                >Retract</button>
            </div>

            <div
                v-if="openId === row.id"
                class="tal__confirm"
                role="alertdialog"
                :aria-labelledby="`tal-confirm-copy-${row.id}`"
                :data-testid="`dla-team-attestation-retract-confirm-${row.id}`"
            >
                <p
                    :id="`tal-confirm-copy-${row.id}`"
                    class="tal__copy"
                >
                    Retract {{ row.team_member_name }}'s statement that this
                    file was already approved? New placements of this file will
                    need the client's approval. Sizes it already approved stay
                    approved.
                </p>
                <label
                    class="tal__label"
                    :for="`tal-note-${row.id}`"
                >Note (optional, only your team sees it)</label>
                <textarea
                    :id="`tal-note-${row.id}`"
                    v-model="note"
                    class="tal__note"
                    rows="2"
                    :maxlength="NOTE_MAX"
                    :data-testid="`dla-team-attestation-retract-note-${row.id}`"
                />
                <div class="tal__actions">
                    <button
                        type="button"
                        class="tal__btn tal__btn--danger"
                        :data-testid="`dla-team-attestation-retract-submit-${row.id}`"
                        :disabled="submitting"
                        @click="submit(row)"
                    >Retract statement</button>
                    <button
                        type="button"
                        class="tal__btn"
                        :data-testid="`dla-team-attestation-retract-cancel-${row.id}`"
                        :disabled="submitting"
                        @click="close"
                    >Cancel</button>
                </div>
                <p
                    v-if="error"
                    class="tal__error"
                    :data-testid="`dla-team-attestation-retract-error-${row.id}`"
                >{{ error }}</p>
            </div>
        </div>

        <p
            v-if="kept"
            class="tal__kept"
            data-testid="dla-team-attestation-retract-kept"
        >
            Retracted. Sizes it already approved are still approved in
            {{ keptStoresLabel }}. Ask the client to approve them if they
            should not be.
        </p>
    </div>
</template>

<script>
import axios from '@/axios'

// The endpoint's limit (prior_approval.RETRACT_NOTE_MAX_LEN).
const NOTE_MAX = 500

export default {
    name: 'TeamAttestationList',
    props: {
        /** The artwork whose statements to list. Nothing renders without it. */
        artworkId: {
            type: [Number, String],
            default: null,
        },
    },
    emits: ['retracted'],
    data() {
        return {
            NOTE_MAX,
            rows: [],
            openId: null,
            note: '',
            submitting: false,
            error: null,
            // The stores still relying on approvals the last retracted
            // statement gave; null when there are none to warn about.
            kept: null,
        }
    },
    computed: {
        listUrl() {
            return this.artworkId == null
                ? null
                : `/api/artworks/${this.artworkId}/prior-approval/team-attestations`
        },
        keptStoresLabel() {
            return (this.kept || [])
                .map((s) => (s.live ? `${s.name} (live)` : s.name))
                .join(', ')
        },
    },
    watch: {
        artworkId: {
            immediate: true,
            handler() {
                this.kept = null
                this.close()
                this.load()
            },
        },
    },
    methods: {
        statementLabel(row) {
            return `Approved before upload by team member ${row.team_member_name}`
        },
        async load() {
            if (!this.listUrl) {
                this.rows = []
                return
            }
            try {
                const resp = await axios.get(this.listUrl)
                const list = resp?.data?.team_attestations
                this.rows = Array.isArray(list) ? list : []
            } catch (err) {
                // The statements are supplementary to the card: a failed
                // read shows none rather than breaking the page.
                this.rows = []
            }
        },
        open(row) {
            this.openId = row.id
            this.note = ''
            this.error = null
        },
        close() {
            this.openId = null
            this.note = ''
            this.error = null
        },
        async submit(row) {
            if (this.submitting || !this.listUrl) return
            const note = String(this.note || '').trim()
            this.submitting = true
            this.error = null
            try {
                const resp = await axios.post(
                    `${this.listUrl}/${row.id}/retract`,
                    note ? { note } : {}
                )
                const result = resp?.data || {}
                const stores = Array.isArray(result.stores_keeping_approval)
                    ? result.stores_keeping_approval
                    : []
                this.kept = stores.length ? stores : null
                this.close()
                await this.load()
                this.$emit('retracted', result)
            } catch (err) {
                const data = err?.response?.data || {}
                this.error =
                    data.error ||
                    'Could not retract this statement. Please try again.'
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style scoped>
.tal {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.tal__row {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.tal__line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
.tal__chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 9px;
    border-radius: var(--tf-radius-pill, 9999px);
    font-size: 11.5px;
    font-weight: 500;
    background: var(--tf-chip-success-bg, #e6f7ed);
    color: var(--tf-success-dark, #12805a);
}
.tal__who {
    font-size: 12px;
    color: var(--tf-fg-4, #616161);
}
.tal__confirm {
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.tal__copy {
    margin: 0;
    font-size: 13px;
    color: var(--tf-fg-2, #424242);
}
.tal__label {
    font-size: 12px;
    color: var(--tf-fg-4, #616161);
}
.tal__note {
    font: inherit;
    font-size: 13px;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 6px;
    padding: 6px 8px;
    resize: vertical;
}
.tal__actions {
    display: flex;
    gap: 8px;
}
.tal__btn {
    font: inherit;
    font-size: 12.5px;
    border: 1px solid var(--tf-border, #e0e0e0);
    background: #ffffff;
    border-radius: 6px;
    padding: 4px 10px;
    cursor: pointer;
}
.tal__btn:disabled {
    opacity: 0.6;
    cursor: default;
}
.tal__btn--link {
    border: none;
    background: none;
    padding: 0;
    color: var(--tf-link, #1565c0);
    text-decoration: underline;
}
.tal__btn--danger {
    background: #b42318;
    border-color: #b42318;
    color: #ffffff;
}
.tal__error {
    margin: 0;
    font-size: 12.5px;
    color: #b42318;
}
.tal__kept {
    margin: 0;
    font-size: 12.5px;
    color: #a15c00;
}
</style>
